import type {
  AdminToken,
  AdminTokenAccessMode,
  AdminTokenModuleGrant,
} from '../types/adminToken';

type TimestampField =
  | 'expiresAt'
  | 'lastUsedAt'
  | 'revokedAt'
  | 'createdAt'
  | 'updatedAt';
type WireTimestamp = number | string;

export type RawAdminToken = Partial<Omit<AdminToken, TimestampField>> & {
  [K in TimestampField]?: WireTimestamp;
} & {
  access_mode?: string;
  module_grants?: Array<{
    module?: string;
    access_mode?: string;
    accessMode?: string;
  }>;
  token_id?: string;
  created_by?: string;
  expires_at?: WireTimestamp;
  last_used_at?: WireTimestamp;
  revoked_at?: WireTimestamp;
  create_at?: WireTimestamp;
  update_at?: WireTimestamp;
};

function timestamp(value: WireTimestamp | undefined): number {
  return Number(value ?? 0);
}

export function normalizeToken(token: RawAdminToken): AdminToken {
  const rawGrants = token.moduleGrants ?? token.module_grants ?? [];
  return {
    accessMode:
      (token.accessMode ?? token.access_mode) === 'read' ? 'read' : 'write',
    moduleGrants: rawGrants.map((grant) => ({
      module: grant.module as AdminTokenModuleGrant['module'],
      accessMode: (grant.accessMode ??
        grant.access_mode) as AdminTokenAccessMode,
    })),
    tokenId: token.tokenId ?? token.token_id ?? '',
    note: token.note ?? '',
    createdBy: token.createdBy ?? token.created_by ?? '',
    expiresAt: timestamp(token.expiresAt ?? token.expires_at),
    lastUsedAt: timestamp(token.lastUsedAt ?? token.last_used_at),
    revokedAt: timestamp(token.revokedAt ?? token.revoked_at),
    createdAt: timestamp(token.createdAt ?? token.create_at),
    updatedAt: timestamp(token.updatedAt ?? token.update_at),
  };
}

export class AdminTokenModeMismatchError extends Error {
  readonly tokenId: string;

  constructor(tokenId: string) {
    super(
      `密钥 ${tokenId || '（未知 ID）'} 的权限与请求不一致，请核查列表并撤销该密钥，勿重复创建`,
    );
    this.name = 'AdminTokenModeMismatchError';
    this.tokenId = tokenId;
  }
}

export function validateCreatedToken(
  token: RawAdminToken,
  mode: AdminTokenAccessMode,
  grants: AdminTokenModuleGrant[] = [],
): AdminToken {
  const normalized = normalizeToken(token);
  const expected = [...grants].sort((a, b) => a.module.localeCompare(b.module));
  const actual = [...normalized.moduleGrants].sort((a, b) =>
    a.module.localeCompare(b.module),
  );
  if (
    (token.accessMode ?? token.access_mode) !== mode ||
    JSON.stringify(actual) !== JSON.stringify(expected)
  ) {
    throw new AdminTokenModeMismatchError(
      token.tokenId ?? token.token_id ?? '',
    );
  }
  return normalized;
}
